import { formatDateTimeFromISO } from "widgets";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { type InputModifyPlaybookEventInput, type PlaybookEvent } from "@/graphql.ts";
import {
  LOAD_EVENT_FAILURE,
  LOAD_EVENT_REQUEST,
  LOAD_EVENT_SUCCESS,
  MODIFY_EVENT_FAILURE,
  MODIFY_EVENT_REQUEST,
  MODIFY_EVENT_SUCCESS,
} from "@/redux/action-types.ts";

import { createReducer, type ReduxAction } from "../redux-utils.js";

/**
 * State definition
 */
export interface IEventState {
  event?: PlaybookEvent;
  isLoading: boolean;
}

const initialState: IEventState = {
  event: undefined,
  isLoading: true,
};

/**
 * Reducers
 */
const eventReducer = createReducer(initialState, {
  [LOAD_EVENT_REQUEST]: (state: IEventState): IEventState => {
    return { ...state, isLoading: true };
  },
  [LOAD_EVENT_SUCCESS]: (state: IEventState, payload: PlaybookEvent): IEventState => {
    return { ...state, isLoading: false, event: payload };
  },
  [LOAD_EVENT_FAILURE]: (state: PlaybookEvent): IEventState => {
    return { ...state, isLoading: false, event: undefined };
  },
  [MODIFY_EVENT_SUCCESS]: (state: IEventState, payload: PlaybookEvent): IEventState => {
    return { ...state, event: payload };
  },
});

/**
 * Action creators
 */

// Last request in time unique identifier
let lastFetchedEventId = -1;

function loadEvent(eventId: number): ReduxAction<Promise<PlaybookEvent>> {
  return (dispatch, getState, webApiClient) => {
    const state = getState();
    lastFetchedEventId = eventId;

    dispatch({ type: LOAD_EVENT_REQUEST });

    return webApiClient
      .fetchEvent(eventId)
      .then((event) => {
        if (lastFetchedEventId === event.id) {
          dispatch({
            type: LOAD_EVENT_SUCCESS,
            payload: event,
          });
          return event;
        }

        return state.event.event;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_EVENT_FAILURE,
          alertTitle: "Fetching of Event Failed",
        }),
      );
  };
}

function loadEvents(
  limit: number,
  categoryId?: number,
  start = 0,
  currentUserOnly = false,
): ReduxAction<Promise<PlaybookEvent[]>> {
  return (dispatch, _getState, webApiClient) => {
    return webApiClient.fetchEvents(limit, categoryId, start, currentUserOnly).catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_EVENT_FAILURE,
        alertTitle: "Fetching of Events Failed",
      }),
    );
  };
}

/******* Utilities **********/

export function getPlaybookEventName(playbookEvent: PlaybookEvent | undefined) {
  if (!playbookEvent) {
    return "";
  }
  if (playbookEvent.alias) {
    return playbookEvent.alias;
  }

  return `${playbookEvent.playbook.name} - ${formatDateTimeFromISO(playbookEvent.created)}`;
}

export function modifyEvent(playbookEvent: InputModifyPlaybookEventInput): ReduxAction<Promise<IEventState>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch({ type: MODIFY_EVENT_REQUEST });

    return webApiClient
      .modifyPlaybookEvent(playbookEvent)
      .then((event) => {
        dispatch({
          type: MODIFY_EVENT_SUCCESS,
          payload: event,
        });
        return event;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: MODIFY_EVENT_FAILURE,
          throwError: true,
          showAlert: false,
        }),
      );
  };
}

export { eventReducer, loadEvent, loadEvents };
